'use client';

import type { PrV2SummaryResponse } from '@/lib/pr-v2/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { fetchPrV2Summary } from './fetch-pr-v2';
import {
  getPrV2CacheSource,
  logPrV2CacheUsage,
  logPrV2InitialDataUsage,
  setPrV2CacheSource,
} from './prefetch-debug';
import { PR_V2_SUMMARY_STALE_TIME_MS } from './query-config';
import { prV2SummaryQueryKey } from './query-keys';

export function usePrV2Summary(
  owner: string,
  repo: string,
  number: string,
  options?: {
    enabled?: boolean;
    initialData?: PrV2SummaryResponse;
    initialDataUpdatedAt?: number;
  },
) {
  const queryClient = useQueryClient();
  const hasInitialData = Boolean(options?.initialData);
  const queryKey = prV2SummaryQueryKey(owner, repo, number);
  const queryEnabled = Boolean(owner && repo && number) && (options?.enabled ?? true);
  const queryHash = JSON.stringify(queryKey);

  const [preMountSnapshot] = useState(() => {
    // Upgrade an inbox click seed with the full server payload before the query
    // observer mounts: initialData only applies to empty cache entries, so on
    // seeded navigations (fast clicks, keyboard, touch — anything that beats the
    // hover prefetch) the RSC payload would otherwise be discarded and the
    // description would block on a serialized client refetch. updatedAt stays 0
    // (immediately stale) unless the server did a live refresh, so the documented
    // freshness contract is unchanged: the mount refetch still fires, it just no
    // longer gates first meaningful paint.
    const existingState = queryClient.getQueryState(queryKey);
    const isInboxSeed =
      existingState?.data !== undefined &&
      existingState.dataUpdatedAt === 0 &&
      getPrV2CacheSource(queryKey) === 'seed:inbox-click';
    if (isInboxSeed && options?.initialData) {
      queryClient.setQueryData(queryKey, options.initialData, {
        updatedAt: options.initialDataUpdatedAt ?? 0,
      });
      setPrV2CacheSource(queryKey, 'server:initial-data');
    }

    return {
      queryHash,
      hasData: queryClient.getQueryData(queryKey) !== undefined,
      source: getPrV2CacheSource(queryKey),
    };
  });

  const isPreMountQuery = preMountSnapshot.queryHash === queryHash;
  const snapshotHasData = isPreMountQuery
    ? preMountSnapshot.hasData
    : queryClient.getQueryData(queryKey) !== undefined;
  const snapshotSource = isPreMountQuery ? preMountSnapshot.source : getPrV2CacheSource(queryKey);

  useEffect(() => {
    if (!queryEnabled) {
      return;
    }

    const prKey = `${owner}/${repo}#${number}`;
    if (snapshotHasData) {
      logPrV2CacheUsage({
        target: 'summary',
        prKey,
        queryKey,
        source: snapshotSource ?? 'cache:unknown',
      });
      return;
    }

    if (hasInitialData) {
      setPrV2CacheSource(queryKey, 'server:initial-data');
      logPrV2InitialDataUsage({
        target: 'summary',
        prKey,
        queryKey,
        source: 'server:initial-data',
      });
    }
  }, [
    hasInitialData,
    number,
    owner,
    queryEnabled,
    queryKey,
    repo,
    snapshotHasData,
    snapshotSource,
  ]);

  return useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchPrV2Summary(owner, repo, number, signal),
    enabled: queryEnabled,
    staleTime: PR_V2_SUMMARY_STALE_TIME_MS,
    // Server-rendered summary data keeps first paint fast. By default it stays immediately stale
    // so the client verifies the current PR head on mount, but callers may override this when the
    // server already refreshed the metadata just before render.
    initialDataUpdatedAt: options?.initialDataUpdatedAt ?? (hasInitialData ? 0 : undefined),
    refetchInterval: false,
    refetchOnWindowFocus: false,
    initialData: options?.initialData,
  });
}
